import type { CanonicalEntity, UniversalBundle } from './types';

/**
 * ВЬЮЕР-СЛАЙС — лёгкая проекция universal-бандла для 3D-вьюера.
 *
 * Бэкенд (`backend/viewer_slice.py`, маршрут
 * `GET /api/models/{id}/viewer-bundle`) отдаёт только то, что рендер и
 * взаимодействие реально читают: canonicalScene-сущности со слитыми
 * визуальными доменами, viewerHints без graphs и комнаты как есть.
 * Производственные секции (modelRows, BOM, cut, смета-проекции) и
 * производственные домен-ключи (drillings, hardware/joinery contracts,
 * материальные снапшоты) в слайс не попадают вовсе.
 *
 * Повторяющиеся документы (паспорта материалов, режимные контракты, профили
 * фурнитуры) вынесены в общие пулы `materialCards` / `sharedContracts`; в
 * домене остаётся ссылка-заглушка. Реидратация происходит ЗДЕСЬ, в одной
 * точке, до отдачи бандла в воркспейс — поэтому ни один потребитель
 * (материал-пасс, видимость, зеркала, план) не знает о слайсе.
 *
 * Старый путь (`404`/старый бэкенд/битый слайс) — полный `/bundle`, как
 * раньше. См. `api.bundle` в client.ts.
 */
export const VIEWER_SLICE_SCHEMA = 'holzcom.viewer-bundle-slice.v1';

export type ViewerBundleSlice = {
  schema: string;
  bundleVersion?: number;
  bundleHash?: string;
  modelName?: string;
  sourceKind: string;
  generatedAt?: string;
  canonicalScene: UniversalBundle['canonicalScene'];
  materialCards?: Record<string, Record<string, unknown>>;
  sharedContracts?: Record<string, Record<string, unknown>>;
  publicationLineage?: Record<string, unknown>;
  // VFA-FE-01/02/03/16: read-only top-level sections that ride through the
  // slice verbatim (the entity slimming stays; these are bundle-level).
  modelRows?: unknown[];
  pro100Review?: Record<string, unknown>;
  kinematicsStatus?: string;
  constructorProject?: Record<string, unknown>;
  sourceAuthority?: Record<string, unknown>;
  sourceRecordCoverage?: Record<string, unknown>;
  nativeProjectSnapshot?: Record<string, unknown>;
};

function plainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function rehydrateCards(
  domain: Record<string, unknown>,
  materialCards: ViewerBundleSlice['materialCards'],
  sharedContracts: ViewerBundleSlice['sharedContracts'],
): void {
  for (const key of Object.keys(domain)) {
    const value = domain[key];
    if (!plainRecord(value)) continue;
    const materialCardId = value.materialCardId;
    if (typeof materialCardId === 'string' && plainRecord(materialCards?.[materialCardId])) {
      domain[key] = materialCards[materialCardId];
      continue;
    }
    const contractCardId = value.contractCardId;
    if (typeof contractCardId === 'string' && plainRecord(sharedContracts?.[contractCardId])) {
      domain[key] = sharedContracts[contractCardId];
    }
  }
}

/**
 * Слайс → форма universal-бандла, которую уже умеет есть весь воркспейс
 * (`exactUniversalBundle`). Бросает исключение на любом несоответствии —
 * вызывающий код отвечает фолбэком на полный бандл.
 */
export function viewerBundleSliceToBundle(slice: ViewerBundleSlice): UniversalBundle {
  if (!plainRecord(slice) || slice.schema !== VIEWER_SLICE_SCHEMA) {
    throw new Error(`VIEWER_SLICE_SCHEMA_INVALID: ${String((slice as { schema?: unknown })?.schema)}`);
  }
  const canonicalScene = slice.canonicalScene;
  if (!plainRecord(canonicalScene) || !Array.isArray(canonicalScene.entities)) {
    throw new Error('VIEWER_SLICE_SCENE_INVALID');
  }
  const materialCards = slice.materialCards;
  const sharedContracts = slice.sharedContracts;
  let entities = canonicalScene.entities as CanonicalEntity[];
  if (materialCards || sharedContracts) {
    entities = entities.map((entity) => {
      const domain = (entity as { domain?: unknown }).domain;
      if (!plainRecord(domain)) return entity;
      const rehydrated = { ...domain };
      rehydrateCards(rehydrated, materialCards, sharedContracts);
      return { ...entity, domain: rehydrated } as CanonicalEntity;
    });
  }
  // VFA-FE-01/02/03/16: the slice carries the read-only top-level sections
  // the panel inspector / constructor recovery / native PRO100 loader read;
  // they ride through verbatim so the rehydrated bundle matches the
  // full-bundle shape on every consumer.
  const rideThrough: Record<string, unknown> = {};
  for (const key of [
    'modelRows', 'pro100Review', 'kinematicsStatus',
    'constructorProject', 'sourceAuthority', 'sourceRecordCoverage', 'nativeProjectSnapshot',
  ] as const) {
    if (slice[key] !== undefined) rideThrough[key] = slice[key];
  }
  return {
    schema: 'holzcom.universal_bundle.v1',
    sourceKind: slice.sourceKind,
    ...(slice.generatedAt !== undefined ? { generatedAt: slice.generatedAt } : {}),
    ...(slice.bundleVersion !== undefined ? { bundleVersion: slice.bundleVersion } : {}),
    ...(slice.bundleHash !== undefined ? { bundleHash: slice.bundleHash } : {}),
    ...(slice.modelName !== undefined ? { modelName: slice.modelName } : {}),
    ...(plainRecord(slice.publicationLineage) && Object.keys(slice.publicationLineage).length > 0
      ? { publicationLineage: slice.publicationLineage } : {}),
    ...rideThrough,
    canonicalScene: { ...canonicalScene, entities },
    viewerSlice: true,
  } as UniversalBundle;
}

/** Прерванный запрос не фолбэчится: отмена есть отмена. Это и явная отмена
 * вызывающего (AbortError), и дедлайн транспорта (TimeoutError «request
 * deadline» — как в raw виде, так и очеловеченный с меткой
 * REQUEST_DEADLINE_EXCEEDED): бэкенд, не ответивший за 12 секунд, вряд ли
 * ответит на повторный запрос полного бандла за следующие 12. */
export function isRequestAbort(error: unknown): boolean {
  const named = error as { name?: unknown; message?: unknown } | null;
  if (named?.name === 'AbortError' || named?.name === 'TimeoutError') return true;
  return typeof named?.message === 'string'
    && named.message.includes('REQUEST_DEADLINE_EXCEEDED');
}
